import { useState } from 'react';
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Label,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FILTER_TYPE_AXIS_TITLES, seriesColumnList } from '../../constants';
import { tokens } from '../../designTokens';
import type { HealthSystemSummaryRow } from '../../interfaces/Dashboard';
import { dropZeroTotalRows, sortByHealthSystemName } from '../../utils/summary';
import { formatCount } from '../../utils/format';

/** Picks legible text for a solid-colour background, e.g. white on purple, black on yellow. */
const getContrastText = (hex: string) => {
  const value = hex.replace('#', '');
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? tokens.colors.primaryText : tokens.colors.white;
};

interface ColumnTooltipPayloadEntry {
  name?: string;
  value?: number;
  color?: string;
  dataKey?: string;
}

interface ColumnTooltipProps {
  active?: boolean;
  label?: string;
  payload?: ColumnTooltipPayloadEntry[];
}

/**
 * List-style tooltip, one row per series with that series' own colour as its
 * background - the same treatment as the donut chart's tooltip, so hovering
 * either chart reads consistently.
 */
const ColumnTooltip = ({ active, label, payload }: ColumnTooltipProps) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const nonZero = payload.filter((entry) => (entry.value ?? 0) > 0);
  if (nonZero.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        backgroundColor: tokens.colors.white,
        borderRadius: tokens.borderRadius.small,
        boxShadow: tokens.shadows.medium,
        padding: '8px 4px',
        minWidth: 160,
      }}
    >
      {label ? (
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            padding: '2px 10px 6px',
            color: tokens.colors.primaryText,
          }}
        >
          {label}
        </div>
      ) : null}
      {nonZero.map((entry) => (
        <div
          key={entry.dataKey ?? entry.name}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            margin: '2px 4px',
            padding: '4px 8px',
            borderRadius: tokens.borderRadius.small,
            backgroundColor: entry.color,
            color: getContrastText(entry.color ?? '#ffffff'),
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <span>{entry.name}</span>
          <span>{formatCount(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * How much of the x-axis is visible initially, per filter type. The legacy
 * amCharts build used a scrollbar with these same start/end fractions because
 * the EHR and Location breakdowns return far too many categories to fit.
 */
const VISIBLE_FRACTION_BY_FILTER_TYPE: Record<string, number> = {
  EHR_WISE: 1,
  PURPOSE_OF_USE_WISE: 1,
  FACILITY_WISE: 0.2,
  LOCATION_WISE: 0.07,
};

interface SummaryColumnChartProps {
  data: HealthSystemSummaryRow[];
  /** Filter type value, e.g. `EHR_WISE` — sets the x-axis caption. */
  filterType: string;
  height?: number;
  /** Disable the range slider for single-category charts. */
  showBrush?: boolean;
  showLegend?: boolean;
  /** Render each bar's value above it. Cramped when many categories are visible. */
  showValueLabels?: boolean;
}

/**
 * Grouped column chart: one bar per status series, one group per category
 * (health system / EHR / location / purpose of use).
 *
 * Replaces the amCharts 4 XYChart from the legacy app. amCharts 4 is
 * end-of-life and commercially licensed; Recharts is MIT and maintained.
 *
 * X-axis category labels are horizontal, matching the legacy chart --
 * `categoryAxis.renderer.labels.template` there never set a rotation.
 */
const SummaryColumnChart = ({
  data,
  filterType,
  height = 500,
  showBrush = true,
  showLegend = true,
  showValueLabels = true,
}: SummaryColumnChartProps) => {
  const [activeField, setActiveField] = useState<string | null>(null);
  const chartData = dropZeroTotalRows(sortByHealthSystemName(data));

  // Only series with at least one non-zero value across the visible
  // categories get a bar or a legend row - an all-zero status (e.g. "Multiple
  // Patients Found" on a quiet EHR) is exactly the kind of null dataset that
  // should not clutter the chart.
  const visibleSeries = seriesColumnList.filter((column) =>
    chartData.some((row) => Number(row[column.field] ?? 0) > 0),
  );

  if (chartData.length === 0 || visibleSeries.length === 0) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: tokens.colors.secondaryText,
        }}
      >
        No data available for the selected filters.
      </div>
    );
  }

  const visibleFraction = VISIBLE_FRACTION_BY_FILTER_TYPE[filterType] ?? 1;
  const visibleCount = Math.max(
    1,
    Math.min(chartData.length, Math.ceil(chartData.length * visibleFraction)),
  );
  const needsBrush = showBrush && visibleCount < chartData.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height }}>
      <div style={{ flex: '1 1 auto', minHeight: 0 }}>
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart
            data={chartData}
            margin={{ top: 24, right: 24, left: 8, bottom: needsBrush ? 8 : 8 }}
            barCategoryGap='10%'
          >
            <CartesianGrid strokeDasharray='3 3' vertical={false} />
            <XAxis
              dataKey='healthSystemName'
              tick={{ fontSize: 11 }}
              interval={0}
              height={40}
            >
              <Label
                value={FILTER_TYPE_AXIS_TITLES[filterType] ?? ''}
                position='insideBottom'
                offset={-4}
                style={{ fontSize: 12, fill: tokens.colors.darkGrey }}
              />
            </XAxis>
            <YAxis
              allowDecimals={false}
              domain={[0, 'auto']}
              tick={{ fontSize: 10 }}
            >
              <Label
                value='Requests'
                angle={-90}
                position='insideLeft'
                style={{ fontSize: 12, fill: tokens.colors.darkGrey }}
              />
            </YAxis>
            <Tooltip content={<ColumnTooltip />} />

            {visibleSeries.map((column) => (
              <Bar
                key={column.field}
                dataKey={column.field}
                name={column.name}
                fill={column.color}
                fillOpacity={
                  activeField === null || activeField === column.field
                    ? 0.85
                    : 0.25
                }
                onMouseEnter={() => setActiveField(column.field)}
                onMouseLeave={() => setActiveField(null)}
              >
                {showValueLabels ? (
                  <LabelList
                    dataKey={column.field}
                    position='top'
                    formatter={(value: number) => formatCount(value)}
                    style={{ fontSize: 10, fill: tokens.colors.darkGrey }}
                  />
                ) : null}
              </Bar>
            ))}

            {needsBrush ? (
              <Brush
                dataKey='healthSystemName'
                height={18}
                travellerWidth={8}
                startIndex={0}
                endIndex={visibleCount - 1}
                stroke={tokens.colors.primary}
                fill='#d9d9d9'
                fillOpacity={0.8}
              />
            ) : null}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {showLegend ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 6,
            paddingTop: 8,
          }}
        >
          {/*
            Chips, not a full-width list: a one-item-per-row list stretched to
            fill the chart's width (which can be very wide on the combined
            Landing-page chart) left a large dead zone to the right of every
            row. Chips only take the width their own label needs and wrap,
            so they read fine both here and in HealthSystemChartGrid's much
            narrower per-health-system cards.
          */}
          {visibleSeries.map((column) => {
            const isActive = activeField === column.field;
            return (
              <div
                key={column.field}
                onMouseEnter={() => setActiveField(column.field)}
                onMouseLeave={() => setActiveField(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  borderRadius: 999,
                  backgroundColor: isActive
                    ? `${column.color}22`
                    : tokens.colors.lightGrey,
                  transition: 'background-color 0.15s ease',
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: column.color,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: isActive ? 700 : 400,
                    color: tokens.colors.primaryText,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column.name}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default SummaryColumnChart;
